import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react/tooltip';
import AntLocalization from '../../utils/AntLocalization';
import AntConfig from '../../utils/AntConfig';
import getGridDisplayValue from '../../utils/AntGridDisplayValue';

import { AntGlobal, AntSession } from '../../utils/AntContext';

const REVIEW_PAGE_SIZE = 12;

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const initColumns = useCallback(() => {
        var ar = [];
        if (config && config.cnfFields instanceof Array) {
            if (props.columns instanceof Array) {
                props.columns.forEach((col) => {
                    if (typeof col === 'object') ar.push({ ...col });
                    else if (typeof col === 'string') ar.push({ dataField: col });
                });
            }

            if (ar.length <= 0)
                config.cnfFields
                    .filter((f) => f.isKey || f.field === 'code' || f.field === 'name')
                    .forEach((f) => {
                        ar.push({ dataField: f.field });
                    });

            if (ar.length <= 0)
                config.cnfFields
                    .filter((f) => String(f.field).indexOf('name') >= 0)
                    .forEach((f) => {
                        if (ar.length <= 3) ar.push({ dataField: f.field });
                    });

            ar.forEach((col) => {
                var colLookupValues = col.lookup && col.lookup.values instanceof Array ? col.lookup.values : null;

                var label = col.label;
                var alignment = col.alignment;

                var found = null;
                if (col.dataField && config && config.cnfFields instanceof Array) {
                    var tableName = col.cnfTable ? col.cnfTable : props.table ? props.table : props.API ? props.API.entityName : null;

                    var strField = col.cnfField ? col.cnfField : col.dataField;
                    found = config.getCnfField(strField, tableName);

                    if (found) {
                        if (found.gridLabel) label = found.gridLabel;
                        else label = found.label;
                        if (found.type == 'bool') alignment = 'center';

                        if (!colLookupValues && found.lookup && found.lookup.values instanceof Array) colLookupValues = found.lookup.values;
                    }
                }
                label = AntLocalization.translateText(label);
                col.caption = label;
                col.alignment = alignment;

                if (colLookupValues instanceof Array) {
                    col.calculateCellValue = (data) => {
                        if (data) {
                            var colValue = data[col.dataField];
                            var valueField = col.valueField
                                ? col.valueField
                                : found && found.lookup && found.lookup.valueField
                                    ? found.lookup.valueField
                                    : 'code';
                            var displayField = col.displayField
                                ? col.displayField
                                : found && found.lookup && found.lookup.displayField
                                    ? found.lookup.displayField
                                    : 'name';
                            if (colValue != null) {
                                var obj = colLookupValues.find((f) => f[valueField] === colValue);
                                if (obj) return obj[displayField];
                            }
                            return colValue;
                        }
                        return null;
                    };
                }
            });
        }
        return ar;
    });

    const isDeletable = () => {
        var isValid = dataRows.length > 0 && dataRows.filter((row) => !row || !row.delete || !row.delete.isDeletable).length <= 0;
        return isValid;
    };

    const onRemoveClick = (data, e) => {
        var key = keyExpr;
        var newData = dataRows.filter((row) => {
            return row[key] != data[key];
        });

        setDataRows(newData);

        if (props.onItemRemoved) props.onItemRemoved(newData);
    };

    const renderStatusIcon = useCallback((info) => {
        if (info.data?.delete?.isDeletable)
            return (
                <div>
                    <Button
                        className="ant-grid-icon ant-icon-valid"
                        type="normal"
                        stylingMode="text"
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        activeStateEnabled={false}
                    >
                        <i className={'dx-icon dx-icon-check'}></i>
                    </Button>
                </div>
            );

        var errors = info.data?.delete?.errors instanceof Array ? info.data.delete.errors.join('\n') : '';
        if (!errors) errors = AntLocalization.translateText({ vi: 'Dòng không đủ điều kiện để xóa', en: 'This row cannot be deleted', fr: 'Ligne non supprimable', es: 'Fila no eliminable', id: 'Baris tidak dapat dihapus', ja: 'この行は削除できません', zh: '此行不可删除', ko: '삭제할 수 없는 행', hi: 'यह पंक्ति हटाई नहीं जा सकती', ar: 'لا يمكن حذف هذا الصف' });
        errors = errors.split('\n');

        var key = keyExpr;
        var id = `ant-grid-error-${props.API.entityName}-${info.data[key]}`;

        return (
            <div>
                <Button
                    className="ant-grid-icon ant-icon-error"
                    id={id}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                    activeStateEnabled={false}
                >
                    <i className={'dx-icon dx-icon-info'}></i>
                </Button>

                <Tooltip
                    target={`#${id}`}
                    className="ant-tooltip"
                    hideEvent="mouseleave"
                    showEvent={{
                        name: 'mouseenter dxclick keyup'
                    }}
                    hideOnOutsideClick={true}
                >
                    <div>
                        {errors.map((item, key) => {
                            if (key < errors.length - 1)
                                return (
                                    <div>
                                        <caption>{item}</caption>
                                        <br />
                                    </div>
                                );
                            else
                                return (
                                    <div>
                                        <caption>{item}</caption>
                                    </div>
                                );
                        })}
                    </div>
                </Tooltip>
            </div>
        );
    });

    const renderRemoveButton = useCallback((info) => {
        return (
            <div>
                <Button
                    className="ant-grid-icon"
                    hint={AntLocalization.translateText({ vi: 'Bỏ dòng này', en: 'Remove this row', fr: 'Retirer la ligne', es: 'Quitar fila', id: 'Buang baris ini', ja: 'この行を除外', zh: '移除此行', ko: '이 행 제외', hi: 'पंक्ति हटाएँ', ar: 'إزالة الصف' })}
                    onClick={(e) => {
                        onRemoveClick(info.data, e);
                    }}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                >
                    <i className={'dx-icon icon dx-icon-trash'}></i>
                </Button>
            </div>
        );
    });

    const load = async (data) => {
        var obj = await props.API.postDeletePrepare(data);
        var newRows = obj && obj.rows ? obj.rows : [];

        var canDelete = newRows.length > 0 && newRows.filter((row) => !row || !row.delete || !row.delete.isDeletable).length <= 0;

        if (props.onValidated) props.onValidated(canDelete);

        setDataRows(newRows);
    };

    const grid = useRef();

    const gridColumns = useMemo(() => initColumns(), []);
    const [dataRows, setDataRows] = useState([]);
    const keyExpr = props.entity_key ? props.entity_key : 'id';

    useEffect(() => {
        if (grid.current && grid.current.instance) grid.current.instance.pageIndex(0);
        load(props.dataRows);
    }, [props.dataRows]);

    useImperativeHandle(ref, () => ({
        isDeletable: isDeletable
    }));

    return (
        <React.Fragment>
            <DataGrid
                ref={grid}
                noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                className={'dx-card wide-card'}
                dataSource={dataRows}
                keyExpr={keyExpr}
                focusedRowEnabled={false}
                columnAutoWidth={true}
                columnHidingEnabled={false}
                showColumnLines={false}
                showRowLines={true}
                showBorders={true}
                rowAlternationEnabled={true}
                repaintChangesOnly={true}
                visible={dataRows.length > 0}
            >
                <Paging pageSize={props.pageSize || REVIEW_PAGE_SIZE} />

                <Pager
                    visible={dataRows.length > (props.pageSize || REVIEW_PAGE_SIZE)}
                    showInfo={true}
                    infoText={`{2} ${AntLocalization.translateText({ vi: 'dòng', en: 'records', fr: 'lignes', es: 'registros', id: 'catatan', ja: 'レコード', zh: '条记录', ko: '개 레코드', hi: 'रिकॉर्ड', ar: 'سجلات' })}`}
                />
                <Column name="remove" caption=" " width={40} alignment="center" cellRender={renderRemoveButton} />
                <Column name="cellStatus" caption=" " width={40} alignment="center" cellRender={renderStatusIcon} />
                {gridColumns.map((col, i) => {
                    return (
                        <Column
                            key={i}
                            {...col}
                            // The original grid column carries its display
                            // formatter. Render its final value while safely
                            // resolving lookup objects returned by delete-prepare.
                            cellRender={(cell) => getGridDisplayValue(cell, col)}
                        />
                    );
                })}
            </DataGrid>
        </React.Fragment>
    );
});
